import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { usdCurrencyFormat } from '../../resources/functions';
import { ApiError, getDepositDetail } from './customerCenterApi';
import { displayMoney } from './money';
import SplitEditor from './SplitEditor';
import type { CustomerAllocation, DepositDetail } from './types';

/**
 * Shows deposit detail and lets users edit customer splits.
 * startEditing opens straight into the split editor (the Deposits to assign flow) and closes after save or cancel.
 */
interface Props {
  branchId: string | number;
  depositId: string | number;
  startEditing?: boolean;
  onClose: () => void;
  onChanged?: () => void;
}

const MONEY_RE = /^\d+(\.\d{1,2})?$/;
const PLACEHOLDER = '--';

/** Parses a validated two-decimal string into cents; rejects anything else. */
function parseCents(s: string): number | null {
  const t = s.trim().replace(/,/g, '');
  if (!MONEY_RE.test(t)) return null;
  const parts = t.split('.');
  const dollars = parseInt(parts[0], 10);
  if (!Number.isFinite(dollars)) return null;
  const centsPart = parts.length === 2 ? parseInt((parts[1] + '00').slice(0, 2), 10) : 0;
  const cents = dollars * 100 + centsPart;
  if (!Number.isSafeInteger(cents) || cents <= 0) return null;
  return cents;
}

function depositTotalCents(detail: DepositDetail | null): number | null {
  if (!detail || detail.deposit_amount === null) return null;
  return parseCents(detail.deposit_amount);
}

function allocationSumCents(detail: DepositDetail | null): number {
  if (!detail) return 0;
  let sum = 0;
  for (const entry of detail.allocations) {
    if (entry.amount === null) continue;
    const cents = parseCents(entry.amount);
    if (cents !== null) sum += cents;
  }
  return sum;
}
function lineTypeLabel(t: (k: string) => string, accountType: string): string {
  switch (accountType) {
    case 'Deposit':
      return t('detailLineTypeDeposit');
    case 'Equity':
      return t('detailLineTypeEquity');
    case 'Bank_Transfer':
      return t('detailLineTypeBankTransfer');
    case 'Others':
      return t('detailLineTypeOthers');
    case 'Adjusted_Bank_Deposit':
      return t('detailLineTypeAdjustedBankDeposit');
    default:
      return accountType;
  }
}

function sourceLabel(t: (k: string) => string, source: string): string {
  switch (source) {
    case 'customer_center':
      return t('detailSourceCustomerCenter');
    case 'deposit_update':
      return t('detailSourceDepositUpdate');
    case 'reimport':
      return t('detailSourceReimport');
    default:
      return source;
  }
}

function allocationsLabel(t: (k: string, o?: Record<string, string>) => string, list: CustomerAllocation[]): string {
  if (list.length === 0) return t('detailAllocNone');
  return list
    .map((row) => t('detailAllocRow', { name: row.customer_name || `#${String(row.customer_id)}`, amount: displayMoney(row.amount) }))
    .join(', ');
}

const CustomerDepositDetail: React.FC<Props> = ({ branchId, depositId, startEditing = false, onClose, onChanged }) => {
  const { t } = useTranslation('scenes.customerCenter');
  const [detail, setDetail] = useState<DepositDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [denied, setDenied] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(startEditing);
  const [saved, setSaved] = useState<boolean>(false);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [showLines, setShowLines] = useState<boolean>(false);

  const fetchCtrl = useRef<AbortController | null>(null);
  const seq = useRef<number>(0);

  const totalCents = useMemo(() => depositTotalCents(detail), [detail]);
  const totalValid = totalCents !== null && totalCents > 0;
  const total = totalValid && totalCents !== null ? totalCents / 100 : 0;

  const allocatedCents = useMemo(() => allocationSumCents(detail), [detail]);
  const allocated = allocatedCents / 100;

  const leftCents = totalValid && totalCents !== null ? totalCents - allocatedCents : 0;
  const unallocated = leftCents / 100;
  const hasSplits = (detail?.allocations.length ?? 0) > 0;

  const breakdowns = useMemo(() => {
    if (!detail) return [];
    return detail.breakdowns.map((b, i) => ({
      key: `${String(b.id)}-${i}`,
      id: String(b.id),
      label: b.account_type ? lineTypeLabel(t, b.account_type) : b.description || PLACEHOLDER,
      amount: b.amount,
    }));
  }, [detail, t]);

  const load = useCallback(() => {
    seq.current += 1;
    const mySeq = seq.current;
    fetchCtrl.current?.abort();
    setDetail(null);
    setError('');
    setDenied(false);
    setEditing(startEditing);
    setSaved(false);
    setShowHistory(false);
    setShowLines(false);
    if (branchId === '' || depositId === '') {
      setLoading(false);
      setError(t('detailMissing'));
      return;
    }
    const ctrl = new AbortController();
    fetchCtrl.current = ctrl;
    setLoading(true);
    getDepositDetail(branchId, depositId, ctrl.signal).then(
      (loaded) => {
        if (seq.current !== mySeq || ctrl.signal.aborted) return;
        setDetail(loaded);
        setLoading(false);
      },
      (err: unknown) => {
        if (seq.current !== mySeq || ctrl.signal.aborted) return;
        setLoading(false);
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          setDenied(true);
          return;
        }
        setError(err instanceof Error && err.message ? err.message : t('detailLoadFailed'));
      },
    );
  }, [branchId, depositId, startEditing, t]);

  useEffect(() => {
    load();
    return () => {
      fetchCtrl.current?.abort();
    };
  }, [load]);

  const title = startEditing ? t('assignTitle') : detail?.description || t('detailTitle');
  const when = detail?.date && moment(detail.date).isValid() ? moment(detail.date).format('MM/DD/YYYY') : detail?.date || '';
  const subtitle = startEditing && detail ? [when, detail.description].filter(Boolean).join(' · ') : '';

  return (
    <Modal show onHide={onClose} size="lg" centered dialogClassName="customer-center-modal" backdrop={editing ? 'static' : true}>
      <Modal.Header>
        <div>
          <Modal.Title>{title}</Modal.Title>
          {subtitle ? <div className="text-muted small mt-1">{subtitle}</div> : null}
        </div>
        {(!editing || startEditing) && (
          <Button variant="link" className="text-secondary fs-4 lh-1 p-0 text-decoration-none" aria-label={t('detailClose')} onClick={onClose}>
            ×
          </Button>
        )}
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" role="status" />
            <span>{t('detailLoading')}</span>
          </div>
        )}
        {!loading && denied && <Alert variant="warning">{t('detailDenied')}</Alert>}
        {!loading && !denied && error && (
          <Alert variant="danger">
            {error}
            <div className="mt-2">
              <Button variant="outline-danger" size="sm" onClick={load}>
                {t('detailRetry')}
              </Button>
            </div>
          </Alert>
        )}
        {!loading && !denied && !error && detail && (
          <>
            {!editing && (
              <div className="d-flex flex-wrap gap-3 mb-3">
                <div>
                  <div className="text-muted small">{t('detailDepositTotal')}</div>
                  <strong>{totalValid ? usdCurrencyFormat(total) : PLACEHOLDER}</strong>
                </div>
                <div>
                  <div className="text-muted small">{t('detailAssigned')}</div>
                  <strong>{usdCurrencyFormat(allocated)}</strong>
                </div>
                <div>
                  <div className="text-muted small">{t('detailLeft')}</div>
                  <strong className={totalValid && leftCents < 0 ? 'text-danger' : undefined}>
                    {totalValid ? usdCurrencyFormat(unallocated) : PLACEHOLDER}
                  </strong>
                  {totalValid && leftCents < 0 && (
                    <span className="text-danger small"> {t('detailOverBy', { amount: usdCurrencyFormat(-unallocated) })}</span>
                  )}
                </div>
                <div>
                  <div className="text-muted small">{t('colDate')}</div>
                  <span>{when || PLACEHOLDER}</span>
                </div>
              </div>
            )}
            {!editing && totalValid && leftCents > 0 && hasSplits && (
              <div className="text-muted small mb-3">{t('detailSaveLeaves', { amount: usdCurrencyFormat(unallocated) })}</div>
            )}
            {!totalValid && <Alert variant="danger">{t('detailTotalInvalid')}</Alert>}

            {!editing && saved && (
              <Alert variant="success" dismissible onClose={() => setSaved(false)}>
                {t('detailSaved')}
              </Alert>
            )}
            {!startEditing && (
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="mb-0">{t('detailSplits')}</h6>
                {!editing && totalValid && (
                  <Button variant="outline-primary" size="sm" onClick={() => { setSaved(false); setEditing(true); }}>
                    {/* fixme: placeholder copy */}
                    {hasSplits ? t('detailEdit') : t('detailAssign')}
                  </Button>
                )}
              </div>
            )}
            {editing ? (
              <SplitEditor
                branchId={branchId}
                detail={detail}
                onSaved={(d) => {
                  onChanged?.();
                  if (startEditing) {
                    toast.success(t('detailSaved'));
                    onClose();
                    return;
                  }
                  setDetail(d);
                  setEditing(false);
                  setSaved(true);
                }}
                onCancel={() => (startEditing ? onClose() : setEditing(false))}
              />
            ) : detail.allocations.length === 0 ? (
              <Alert variant="info">
                {t('detailNoSplits', { amount: totalValid ? usdCurrencyFormat(total) : PLACEHOLDER })}
              </Alert>
            ) : (
              <div className="table-responsive mb-2">
                <table className="table table-default">
                  <thead>
                    <tr>
                      <th>{t('colCustomer')}</th>
                      <th className="text-end">{t('detailColAmount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.allocations.map((row) => (
                      <tr key={String(row.customer_id)}>
                        <td>
                          {row.customer_name || t('detailUnavailableCustomer')}
                          {row.active === false && (
                            <Badge bg="light" text="dark" className="ms-2 border">
                              {t('detailInactive')}
                            </Badge>
                          )}
                        </td>
                        <td className="text-end">{displayMoney(row.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!editing && detail.last_changed && (
              <div className="text-muted small mb-2">
                {t('detailLastChanged', { name: detail.last_changed.by_name || t('detailUnknownUser') })}
                {detail.last_changed.at && <>{' '}{t('detailChangedOn', { date: new Date(detail.last_changed.at).toLocaleString() })}</>}
              </div>
            )}

            {!editing && detail.history.length > 0 && (
              <div className="mb-2">
                <Button variant="link" size="sm" className="p-0" onClick={() => setShowHistory((v) => !v)}>
                  {showHistory ? t('detailHideHistory') : t('detailShowHistory')}
                </Button>
                {showHistory && (
                  <ul className="list-unstyled small mt-2 mb-0">
                    {detail.history.map((h, i) => (
                      <li key={`${h.at ?? 'na'}-${i}`} className="mb-2">
                        <div>
                          {t('detailHistoryLine', { date: h.at ? new Date(h.at).toLocaleString() : PLACEHOLDER, user: h.by_name || t('detailUnknownUser'), source: sourceLabel(t, h.source) })}
                        </div>
                        <div className="text-muted">
                          {t('detailHistoryChange', { before: allocationsLabel(t, h.before), after: allocationsLabel(t, h.after) })}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="customer-center__lines">
              <Button variant="link" size="sm" className="p-0" aria-expanded={showLines} onClick={() => setShowLines((v) => !v)}>
                {showLines ? t('detailHideLines') : t('detailShowLines')}
              </Button>
              {showLines && (
                breakdowns.length === 0 ? (
                  <Alert variant="info" className="mt-2 mb-0">{t('detailNoBreakdowns')}</Alert>
                ) : (
                  <div className="table-responsive mt-2">
                    <table className="table table-default mb-0">
                      <thead>
                        <tr>
                          <th>{t('detailColType')}</th>
                          <th className="text-end">{t('detailColAmount')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdowns.map((b) => (
                          <tr key={b.key}>
                            <td>{b.label}</td>
                            <td className="text-end">{displayMoney(b.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </Modal.Body>
      {!editing && (
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose}>
            {t('detailClose')}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default CustomerDepositDetail;
